import { showErrorToast, showToast } from '~/utils/toast-notifications';

import { ActionsCreator } from '@/store/utils';
import { PortfolioTransactionTypes } from '~/utils/constants/portfolioConstants';
import { eventManager } from '~/utils/behavior/applicationEvents/event-manager';

// Sell return is calculated on BE, so we take it from the newest Sell operation of the returned portfolio
const getSellReturnPercent = (portfolio, itemId) => {
  const latestSell = portfolio?.items
    ?.find((i) => i.id === itemId)
    ?.operations?.filter((o) => o.operationType === 'Sell')
    .sort((a, b) => new Date(b.createdAtUtc) - new Date(a.createdAtUtc))[0];

  return latestSell?.sellReturnPercent ?? null;
};

export default {
  addPortfolioItem(context, payload) {
    context.commit('ADD_PORTFOLIO_ITEM_INIT');
    const profileId = context.rootGetters['authentication/profileId'];
    return this.dispatch('api/addPortfolioItem', payload)
      .then((res) => {
        payload.transactionType = PortfolioTransactionTypes.TRANSACTION;
        window.$nuxt.$notify.addTransactionSuccess(
          window.$nuxt.$t('operation_add_ok'),
          profileId,
          {},
          payload
        );
        context.commit('ADD_PORTFOLIO_ITEM_SUCCESS', res.data);

        if (
          context.state.getPortfolioById.data?.isListed === false &&
          res.data.isListed
        )
          window.$nuxt.$notify.success(
            window.$nuxt.$t('portfolio_go_ranked_message')
          );

        // Raise transaction completed event
        eventManager.raiseTransactionCompletedEvent({
          transaction_type: 'Assets',
          // If adding a new item, we always buy it
          operation_type: 'Buy',
          active_name: payload.companyName,
          currency: payload.chosenCurrency,
          // TODO: Discuss if we add cash info in the future
          action_quantity: payload.itemQuantity,
          transaction_total: payload.itemPrice * payload.itemQuantity,
        });
      })
      .catch((err) => {
        showErrorToast(window.$nuxt.$t('operation_transaction_error_message'));
        context.commit('ADD_PORTFOLIO_ITEM_ERROR', err);
      });
  },
  createOperation(context, payload) {
    context.commit('CREATE_OPERATION_START');
    const profileId = context.rootGetters['authentication/profileId'];
    return this.dispatch('api/createOperation', payload)
      .then((res) => {
        const notificationPayload = {
          ...payload,
          transactionType: PortfolioTransactionTypes.TRANSACTION,
        };

        if (payload.operationType === 'Sell')
          notificationPayload.changePercent = getSellReturnPercent(
            res.data,
            payload.itemId
          );
        window.$nuxt.$notify.addTransactionSuccess(
          window.$nuxt.$t('operation_add_ok'),
          profileId,
          {},
          notificationPayload
        );
        context.commit('CREATE_OPERATION_SUCCESS', res.data);
        context.commit('UPDATE_PORTFOLIO_LINES', res.data);

        if (
          context.state.getPortfolioById.data?.isListed === false &&
          res.data.isListed
        )
          window.$nuxt.$notify.success(
            window.$nuxt.$t('portfolio_go_ranked_message')
          );

        // Raise transaction completed event
        eventManager.raiseTransactionCompletedEvent({
          transaction_type: 'Assets',
          operation_type: payload.operationType,
          active_name: payload.companyName,
          currency: payload.chosenCurrency,
          action_quantity: payload.quantity,
          // This will always be the amount in the stock's currency
          // So if the user choses another currenyc to pay with, the total won't match the currency param here
          // TODO - Add some conversion information to handle this type of case
          transaction_total: payload.price * payload.quantity,
        });
      })
      .catch((err) => {
        if (err.statusCode === 400 && err.errorCode === 'wal_400006') {
          window.$nuxt.$notify.error(
            window.$nuxt.$t('operation_add_error_no_shares_at_date')
          );
        } else if (err.statusCode === 400 && err.errorCode === 'wal_400014') {
          window.$nuxt.$notify.error(
            window.$nuxt.$t('operation_add_error_not_enough_cash')
          );
        } else {
          window.$nuxt.$notify.error(
            window.$nuxt.$t('operation_add_error_general')
          );
        }
        context.commit('CREATE_OPERATION_ERROR', err);
      });
  },
  createCashOperation(context, payload) {
    context.commit('CREATE_CASH_OPERATION_START');
    return this.dispatch('api/createCashOperation', payload)
      .then((res) => {
        window.$nuxt.$notify.success(window.$nuxt.$t('operation_add_ok'));
        context.commit('CREATE_CASH_OPERATION_SUCCESS', res.data);

        // Raise transaction completed event
        eventManager.raiseTransactionCompletedEvent({
          transaction_type: 'Cash',
          operation_type: payload.operationType,
          active_name: payload.companyName,
          currency: payload.currencyCode,
          transaction_total: payload.amount,
          action_quantity: 1,
        });
      })
      .catch((err) => {
        window.$nuxt.$notify.error(
          window.$nuxt.$t('operation_add_error_general')
        );
        context.commit('CREATE_CASH_OPERATION_ERROR', err);
      });
  },
  getTickerStockValue(context, payload) {
    context.commit('GET_TICKER_STOCK_VALUE_INIT');
    return this.dispatch('api/getTickerStockValue', payload)
      .then((res) => {
        context.commit('GET_TICKER_STOCK_VALUE_SUCCESS', res.data);
      })
      .catch((err) => {
        context.commit('GET_TICKER_STOCK_VALUE_INIT_ERROR', err);
      });
  },

  getSimilarTickers(context, payload) {
    context.commit('GET_SIMILAR_TICKERS_INIT');
    return this.dispatch('api/getSimilarTickers', payload)
      .then((res) => {
        context.commit('GET_SIMILAR_TICKERS_SUCCESS', res.data);
      })
      .catch((err) => {
        context.commit('GET_SIMILAR_TICKERS_INIT_ERROR', err);
      });
  },

  getCryptoTickerStockRangeValue(context, payload) {
    context.commit('GET_TICKER_VALUE_INIT');
    return this.dispatch('api/getCryptoTickerStockRangeValue', payload)
      .then((res) => {
        context.commit('GET_TICKER_VALUE_SUCCESS', res.data);
      })
      .catch((err) => {
        context.commit('GET_TICKER_VALUE_INIT_ERROR', err);
      });
  },

  getTickerStockRangeValue(context, payload) {
    const { noSkeletons, ...rest } = payload ?? {};

    if (!noSkeletons) context.commit('GET_TICKER_VALUE_INIT');

    return this.dispatch('api/getTickerStockRangeValue', rest)
      .then((res) => {
        context.commit('GET_TICKER_VALUE_SUCCESS', res.data);
        return res;
      })
      .catch((err) => {
        context.commit('GET_TICKER_VALUE_INIT_ERROR', err);
      });
  },
  setTickerStockRangeValueSuccess(context, payload) {
    context.commit('GET_TICKER_VALUE_SUCCESS', payload.res.data);
  },

  getTickerStockRangeWithOverall(context, payload) {
    const { noSkeletons, ...rest } = payload ?? {};

    if (!noSkeletons) context.commit('GET_TICKER_VALUE_WITH_PERFORMANCE_INIT');

    return this.dispatch('api/getTickerStockRangeValue', rest)
      .then((res) => {
        context.commit('GET_TICKER_VALUE_WITH_PERFORMANCE_SUCCESS', res.data);
      })
      .catch((err) => {
        context.commit('GET_TICKER_VALUE_WITH_PERFORMANCE_ERROR', err);
      });
  },

  resetTickerStockRangeWithOverallData(context) {
    context.commit('GET_TICKER_VALUE_WITH_PERFORMANCE_INIT');
  },

  getMultipleTickerStockRangeValue(context, payload) {
    return this.dispatch('api/getMultipleTickerStockRangeValue', payload)
      .then((res) => {
        context.commit('GET_MULTIPLE_TICKER_VALUE_SUCCESS', res.data);
      })
      .catch((err) => {
        context.commit('GET_MULTIPLE_TICKER_VALUE_INIT_ERROR', err);
      });
  },

  getMultipleTickerStockRangeValueWithOverall(context, payload) {
    return this.dispatch('api/getMultipleTickerStockRangeValue', payload)
      .then((res) => {
        context.commit(
          'GET_MULTIPLE_TICKER_VALUE_WITH_PERFORMANCE_SUCCESS',
          res.data
        );
      })
      .catch((err) => {
        context.commit('GET_MULTIPLE_TICKER_VALUE_WITH_PERFROMANCE_ERROR', err);
      });
  },

  getTeamPortfolioProfiles(context, payload) {
    context.commit('GET_TEAM_PORTFOLIO_PROFILES_INIT');
    return this.dispatch('api/getTeamPortfolioProfiles', payload)
      .then((res) => {
        context.commit('GET_TEAM_PORTFOLIO_PROFILES_SUCCESS', res.data);
      })
      .catch((err) => {
        context.commit('GET_TEAM_PORTFOLIO_PROFILES_INIT_ERROR', err);
      });
  },

  updateTeamPortfolioProfile(context, payload) {
    context.commit('UPDATE_TEAM_PORTFOLIO_PROFILE_INIT');
    return this.dispatch('api/updateTeamPortfolioProfile', payload)
      .then((res) => {
        context.commit('UPDATE_TEAM_PORTFOLIO_PROFILE_SUCCESS', res.data);
      })
      .catch((err) => {
        context.commit('UPDATE_TEAM_PORTFOLIO_PROFILE_ERROR', err);
      });
  },

  deleteTeamPortfolioProfile(context, payload) {
    context.commit('DELETE_TEAM_PORTFOLIO_PROFILE_INIT');
    return this.dispatch('api/deleteTeamPortfolioProfile', payload)
      .then((res) => {
        context.commit('DELETE_TEAM_PORTFOLIO_PROFILE_SUCCESS', res.data);
      })
      .catch((err) => {
        context.commit('DELETE_TEAM_PORTFOLIO_PROFILE_ERROR', err);
      });
  },

  createTeamPortfolioProfile(context, payload) {
    context.commit('CREATE_TEAM_PORTFOLIO_PROFILE_INIT');
    return this.dispatch('api/createTeamPortfolioProfile', payload)
      .then((res) => {
        context.commit('CREATE_TEAM_PORTFOLIO_PROFILE_SUCCESS', res.data);
      })
      .catch((err) => {
        context.commit('CREATE_TEAM_PORTFOLIO_PROFILE_ERROR', err);
      });
  },

  initPortfolioSettings(context) {
    context.commit('PORTFOLIO_SETTINGS_INIT');
  },
  updatePortfolio(context, payload) {
    const showDefaultToast = payload.showDefaultToast ?? true;
    context.commit('UPDATE_PORTFOLIO_INIT');
    return this.dispatch('api/updatePortfolio', payload)
      .then((res) => {
        // TODO: improve this on the API side. Either evaluate the conditions and provide them fully, or make the API clearly indicate it's not loading/evaluating any of it.
        // NOTE: The allocation rule isn't evaluated by the API when updating a portfolio, so the allocation rule comes back as false by default.
        // The conditions shouldn't change from the PF update endpoint. We assume it's safe to retain the current state of the privacy conditions.
        res.data.privacyConditions = null;

        context.commit('UPDATE_PORTFOLIO_SUCCESS', res.data);
        if (showDefaultToast) {
          window.$nuxt.$notify.success(window.$nuxt.$t('portfolio_update_ok'));
        }
        return res;
      })
      .catch((err) => {
        console.error('updatePortfolio error', err);
        context.commit('UPDATE_PORTFOLIO_ERROR', err);
        throw err;
      });
  },

  deletePortfolio(context, payload) {
    context.commit('DELETE_PORTFOLIO_INIT');
    return this.dispatch('api/removePortfolio', payload)
      .then((res) => {
        context.commit('DELETE_PORTFOLIO_SUCCESS', res.data);
        showToast(window.$nuxt.$t('delete_portfolio_success'));
      })
      .catch((err) => {
        context.commit('DELETE_PORTFOLIO_ERROR', err);
        showErrorToast(window.$nuxt.$t('delete_portfolio_failed'));
      });
  },

  likePortfolio(context, payload) {
    return this.dispatch('api/likePortfolio', payload)
      .then((res) => {
        context.commit('LIKE_PORTFOLIO_SUCCESS', res.data);
      })
      .catch((err) => {
        context.commit('LIKE_PORTFOLIO_ERROR', err);
      });
  },

  // Portfolio Favorite
  getFavoritePortfolio(context, payload) {
    context.commit('GET_FAVORITE_PORTFOLIO_INIT');
    return this.dispatch('api/getFavoritePortfolio', payload).then((res) => {
      context.commit('GET_FAVORITE_PORTFOLIO_SUCCESS', res.data);
    });
    // TODO : Handle errors
  },
  favoritePortfolio(context, payload) {
    context.commit('FAVORITE_PORTFOLIO_INIT');
    return this.dispatch('api/favoritePortfolio', payload).then((res) => {
      context.commit('FAVORITE_PORTFOLIO_SUCCESS', {
        id: payload,
        data: res.data,
      });
      return res;
    });
    // TODO : Handle errors
  },
  favoritePortfolioWithoutUpdate(context, payload) {
    context.commit('FAVORITE_PORTFOLIO_INIT');
    return this.dispatch('api/favoritePortfolio', payload).then((res) => {
      return res;
    });
    // TODO : Handle errors
  },
  getMyFavoritePortfolios(context, payload) {
    const { noSkeletons } = payload ?? {};

    // NOTE: With this in place, we only show skeletons if we don't explicitly specify that we don't want skeletons,
    // AND the related data hasn't ALREADY been loaded.
    // This is to prevent unnecessary skeleton showing if the data has already been loaded.
    // Instead, we simply replace the existing data and the UI updates accordingly.
    if (!noSkeletons && context.state.myFavoritePortfolios.data === null)
      context.commit('SET_MY_FAVORITE_PORTFOLIOS_LOADING', true);

    return this.dispatch('api/getMyFavoritePortfolios', payload)
      .then((res) => {
        context.commit('GET_MY_FAVORITE_PORTFOLIOS_SUCCESS', res.data);
      })
      .finally(() => {
        context.commit('GET_MY_FAVORITE_PORTFOLIOS_LOADING', false);
      });
    // TODO : Handle errors
  },

  getPortfolioTopGainers(context, payload) {
    const { noSkeletons } = payload?.noSkeletons ?? {};

    // NOTE: With this in place, we only show skeletons if we don't explicitly specify that we don't want skeletons,
    // AND the related data hasn't ALREADY been loaded.
    // This is to prevent unnecessary skeleton showing if the data has already been loaded.
    // Instead, we simply replace the existing data and the UI updates accordingly.
    if (!noSkeletons && context.state.portfolioTopGainers.data === null)
      context.commit('SET_PORTFOLIOS_TOP_GAINERS_LOADING', true);

    return this.dispatch('api/getPortfolioTopGainers', {
      limit: 10,
      page: 1,
      // Defaulting to 1 month
      from: payload?.from ?? 'M',
    })
      .then((res) => {
        context.commit('GET_PORTFOLIOS_TOP_GAINERS_SUCCESS', res.data.data);
      })
      .catch((err) => {
        context.commit('GET_PORTFOLIOS_TOP_GAINERS_ERROR', err);
      });
  },
  likePortfolioTopGainer(context, payload) {
    return this.dispatch('api/likePortfolio', payload).then((res) => {
      context.commit('LIKE_PORTFOLIO_TOP_GAINER_SUCCESS', {
        id: payload,
        socials: res.data,
      });
    });
    // TODO : handle error
  },
  favoritePortfolioTopGainer(context, payload) {
    return this.dispatch('api/favoritePortfolio', payload).then((res) => {
      context.commit('FAVORITE_PORTFOLIO_SHIFTED_TOP_GAINER_SUCCESS', {
        id: payload,
        socials: res.data,
      });
    });
    // TODO : handle error
  },
  favoritePortfolioMainLeagueTopGainer(context, payload) {
    return this.dispatch('api/favoritePortfolio', payload).then((res) => {
      context.commit('FAVORITE_PORTFOLIO_MAIN_LEAGUE_TOP_GAINER_SUCCESS', {
        id: payload,
        socials: res.data,
      });
    });
  },
  likePortfolioMainLeagueTopGainer(context, payload) {
    return this.dispatch('api/likePortfolio', payload).then((res) => {
      context.commit('LIKE_PORTFOLIO_MAIN_LEAGUE_TOP_GAINER_SUCCESS', {
        id: payload,
        socials: res.data,
      });
    });
  },
  /**
   * Gets the top portfolios from the main league
   * @param {*} context
   * @param {*} payload
   * @returns
   */
  getMainLeaguePortfolioTopGainers(context, payload) {
    const { noSkeletons } = payload ?? {};

    // NOTE: With this in place, we only show skeletons if we don't explicitly specify that we don't want skeletons,
    // AND the related data hasn't ALREADY been loaded.
    // This is to prevent unnecessary skeleton showing if the data has already been loaded.
    // Instead, we simply replace the existing data and the UI updates accordingly.
    if (!noSkeletons && context.state.shiftedPortfolioTopGainers.data === null)
      context.commit('SET_MAIN_LEAGUE_PORTFOLIO_TOP_GAINERS_LOADING', true);

    return this.dispatch('api/getMainLeaguePortfolioTopGainers', {
      limit: 25,
      page: 1,
    })
      .then((res) => {
        context.commit(
          'GET_MAIN_LEAGUE_PORTFOLIO_TOP_GAINERS_SUCCESS',
          res.data
        );
      })
      .catch((err) => {
        context.commit('GET_MAIN_LEAGUE_PORTFOLIO_TOP_GAINERS_ERROR', err);
      });
  },
  favoritePortfolioShiftedTopGainer(context, payload) {
    return this.dispatch('api/favoritePortfolio', payload).then((res) => {
      context.commit('FAVORITE_PORTFOLIO_SHIFTED_TOP_GAINER_SUCCESS', {
        id: payload,
        socials: res.data,
      });
      // TODO : handle errors
    });
  },
  unfavoriteFavoritePortfolio(context, payload) {
    return this.dispatch('api/favoritePortfolio', payload).then(() => {
      context.commit('UNFAVORITE_FAVORITE_PORTFOLIO_SUCCESS', {
        id: payload,
      });
    });
    // TODO: handle errors
  },
  likePortfolioShiftedTopGainer(context, payload) {
    return this.dispatch('api/likePortfolio', payload).then((res) => {
      context.commit('LIKE_PORTFOLIO_SHIFTED_TOP_GAINER_SUCCESS', {
        id: payload,
        socials: res.data,
      });
      // TODO : handle errors
    });
  },
  // Profile Portfolios
  getProfilePortfolios(context, payload) {
    context.commit('GET_PROFILE_PORTFOLIOS_INIT');
    return this.dispatch('api/getProfilePortfolios', payload)
      .then((res) => {
        context.commit('GET_PROFILE_PORTFOLIOS_SUCCESS', res.data);
      })
      .catch((err) => {
        context.commit('GET_PROFILE_PORTFOLIOS_ERROR', err);
      });
  },
  likeProfilePortfolio(context, payload) {
    return this.dispatch('api/likePortfolio', payload.portfolioId).then(
      (res) => {
        if (payload.isMyPortfolio) {
          context.commit('LIKE_MY_PORTFOLIO_SUCCESS', {
            id: payload.portfolioId,
            socials: res.data,
          });
        } else {
          context.commit('LIKE_PROFILE_PORTFOLIO_SUCCESS', {
            id: payload.portfolioId,
            socials: res.data,
          });
        }
      }
    );
    // TODO : handle error
  },
  likeMyPortfolio(context, payload) {
    return this.dispatch('api/likePortfolio', payload).then((res) => {
      context.commit('LIKE_MY_PORTFOLIO_SUCCESS', {
        id: payload,
        socials: res.data,
      });
    });
    // TODO : handle error
  },
  favoriteProfilePortfolio(context, payload) {
    return this.dispatch('api/favoritePortfolio', payload).then((res) => {
      context.commit('FAVORITE_PROFILE_PORTFOLIO_SUCCESS', {
        id: payload,
        socials: res.data,
      });
    });
  },
  /// Portfolio Comments
  getReplies(context, payload) {
    context.commit('INIT_PAGE');
    context.commit('SET_REPLIES_LOADING', true);
    return this.dispatch('api/getPortfolioComments', payload)
      .then((res) => {
        context.commit('SET_REPLIES', res.data);
      })
      .catch((err) => {
        console.dir(err);
        context.commit('SET_REPLIES_ERROR', err);
      })
      .finally(() => {
        context.commit('SET_REPLIES_LOADING', false);
      });
  },
  getNextReplies(context, payload) {
    context.commit('SET_NEXT_REPLIES_LOADING', true);
    return this.dispatch('api/getPortfolioComments', payload)
      .then((res) => {
        context.commit('APPEND_REPLIES', res.data);
      })
      .catch((err) => {
        console.error(err);
        context.commit('SET_REPLIES_ERROR', err);
      })
      .finally(() => {
        context.commit('SET_NEXT_REPLIES_LOADING', false);
      });
  },
  incrementPortfolioReplyCount(context) {
    context.commit('INCREMENT_COMMENT_COUNT');
  },
  incrementPortfolioRepostCount(context) {
    context.commit('INCREMENT_PORTFOLIO_REPOST_COUNT');
  },
  favoriteReply(context, payload) {
    this.dispatch('api/favoritePost', payload)
      .then((res) => {
        context.commit('UPDATE_REPLY', res.data);
      })
      .catch((err) => {
        context.commit('SET_ERROR', err);
      });
  },
  likeReply(context, payload) {
    context.commit('UPDATE_REPLY_LIKE_OPTIMISTIC', payload);
    this.dispatch('api/likePost', payload.replyId)
      .then((res) => {
        context.commit('UPDATE_REPLY', res.data);
      })
      .catch((err) => {
        context.commit('ROLLBACK_REPLY_LIKE', payload);
        context.commit('SET_REPLIES_ERROR', err);
      });
  },
  likeNestedReply(context, payload) {
    context.commit('UPDATE_NESTED_REPLY_LIKE_OPTIMISTIC', payload);
    this.dispatch('api/likePost', payload.nestedReplyId)
      .then((res) => {
        context.commit('UPDATE_NESTED_REPLY', {
          replyId: payload.replyId,
          nestedReply: res.data,
        });
      })
      .catch((err) => {
        context.commit('ROLLBACK_NESTED_REPLY_LIKE', payload);
        context.commit('SET_REPLIES_ERROR', err);
      });
  },
  incrementPostReplyCount(context) {
    context.commit('INCREMENT_POST_REPLYCOUNT');
  },

  incrementNestedReplyReplyCount(context, payload) {
    context.commit('INCREMENT_NESTED_REPLY_REPLYCOUNT', payload);
  },
  incrementReplyReplyCount(context, payload) {
    context.commit('INCREMENT_REPLY_REPLYCOUNT', payload.postId);
  },
  incrementPostRepostCount(context) {
    context.commit('INCREMENT_POST_REPOSTCOUNT');
  },
  incrementReplyRepostCount(context, payload) {
    context.commit('INCREMENT_REPLY_REPOSTCOUNT', payload.postId);
  },
  incrementNestedReplyRepostCount(context, payload) {
    context.commit('INCREMENT_NESTED_REPLY_REPOSTCOUNT', payload);
  },
  appendNewReply(context, data) {
    context.commit('APPEND_NEW_REPLY', data);
  },
  postComment(context, payload) {
    return this.dispatch('api/createPost', payload)
      .then((res) => {
        showToast(window.$nuxt.$t('post_created_ok'));
        context.commit('APPEND_NEW_REPLY', res.data);
      })
      .catch((err) => {
        showErrorToast(window.$nuxt.$t('error_occured_message'));
        console.dir(err);
        context.commit('SET_REPLIES_ERROR', err);
      });
  },
  updateReply(context, payload) {
    return this.dispatch('api/updatePost', payload.data)
      .then((res) => {
        showToast(window.$nuxt.$t('post_updated_ok'));
        res.data.updatedDate = payload.data.updatedDate;
        res.data.postId = payload.postId;
        context.commit('UPDATE_REPLY_AFTER_EDIT', {
          replyId: payload.replyId,
          data: res.data,
        });
      })
      .catch((err) => {
        showErrorToast(window.$nuxt.$t('error_occured_message'));
        context.commit('SET_REPLIES_ERROR', {
          err: err,
          postId: payload.target,
        });
      });
  },
  updateNestedReply(context, payload) {
    return this.dispatch('api/updatePost', payload.data)
      .then((res) => {
        showToast(window.$nuxt.$t('post_updated_ok'));
        res.data.updatedDate = payload.data.updatedDate;
        context.commit('UPDATE_NESTED_REPLY_AFTER_EDIT', {
          replyId: payload.replyId,
          nestedReplyId: payload.nestedReplyId,
          data: res.data,
        });
      })
      .catch((err) => {
        showErrorToast(window.$nuxt.$t('error_occured_message'));
        context.commit('SET_REPLIES_ERROR', {
          err: err,
          postId: payload.data.target,
        });
      });
  },
  getNestedReplies(context, payload) {
    context.commit('SET_REPLIES_LOADING', true);
    this.dispatch('api/getPostReplies', payload)
      .then((res) => {
        if (res.data && res.data.length > 0)
          context.commit('SET_NESTED_REPLIES', {
            postId: payload.postId,
            replies: res.data,
          });
        context.commit('SET_NESTED_REPLY_VIEW_MORE', {
          postId: payload.postId,
          show: res.data.length == payload.params.limit,
        });
      })
      .catch((err) => {
        console.dir(err);
        context.commit('SET_REPLIES_ERROR', err);
      });
  },
  getNextNestedReplies(context, payload) {
    this.dispatch('api/getPostReplies', payload)
      .then((res) => {
        if (res.data && res.data.length > 0)
          context.commit('APPEND_NESTED_REPLIES', {
            postId: payload.postId,
            replies: res.data,
          });
        context.commit('SET_NESTED_REPLY_VIEW_MORE', {
          postId: payload.postId,
          show: res.data.length == payload.params.limit,
        });
      })
      .catch((err) => {
        console.dir(err);
        context.commit('SET_REPLIES_ERROR', err);
      });
  },
  appendNewNestedReply(context, payload) {
    const parentReply = context.state.replies.find(
      (r) => r.postId == payload.postId
    );
    if (parentReply.showLoadMore) return;

    context.commit('APPEND_NEW_NESTED_REPLY', payload);
  },
  setRepliesLoading(context, isLoading) {
    context.commit('SET_REPLIES_LOADING', isLoading);
  },
  setIsLoadFinished(context, isLoadFinished) {
    context.commit('SET_IS_LOAD_FINISHED', isLoadFinished);
  },
  deletePortfolioComment(context, payload) {
    return this.dispatch('api/deleteUserPost', payload)
      .then((res) => {
        context.commit('DELETE_PORTFOLIO_COMMENT_SUCCESS', res.data);
        showToast(window.$nuxt.$t('post_deletion_successful'));
      })
      .catch((err) => {
        context.commit('DELETE_PORTFOLIO_COMMENT_ERROR', err);
        showErrorToast(window.$nuxt.$t('error_occured_message'));
      });
  },
  deleteNestedPortfolioComment(context, payload) {
    return this.dispatch('api/deleteUserPost', {
      postId: payload.nestedReplyId,
    })
      .then((res) => {
        context.commit('DELETE_NESTED_PORTFOLIO_COMMENT_SUCCESS', {
          postId: payload.postId,
          nestedReplyId: res.data,
        });
        showToast(window.$nuxt.$t('post_deletion_successful'));
      })
      .catch((err) => {
        context.commit('DELETE_NESTED_PORTFOLIO_COMMENT_ERROR', err);
        showErrorToast(window.$nuxt.$t('error_occured_message'));
      });
  },

  // Portfolio search
  portfolioSearch(context, payload) {
    context.commit('PORTFOLIO_SEARCH_INIT');
    return this.dispatch('api/portfolioSearch', payload)
      .then((res) => {
        context.commit('PORTFOLIO_SEARCH_SUCCESS', res.data);
      })
      .catch((err) => {
        context.commit('PORTFOLIO_SEARCH_ERROR', err);
      });
  },
  // Portfolios To Compare
  resetPortfolioToCompare(context) {
    context.commit('PORTFOLIO_TO_COMPARE_INIT');
  },
  resetPortfolioSearch(context) {
    context.commit('PORTFOLIO_SEARCH_INIT');
  },
  addPortfoliosToCompare(context, payload) {
    context.commit('ADD_PORTFOLIOS_TO_COMPARE', payload);
  },
  removePortfolioFromComparison(context, portfolioId) {
    context.commit('REMOVE_PORTFOLIO_TO_COMPARE', portfolioId);
  },
  removeAllPortfolioFromComparison(context) {
    context.commit('REMOVE_ALL_PORTFOLIOS_FROM_COMPARISON');
  },
  getPortfolioComparisonGraphInfo(context, payload) {
    return this.dispatch('api/getPortfolioComparisonGraphInfo', payload)
      .then((res) => {
        context.commit('GET_PORTFOLIO_COMPARISON_GRAPH_INFO_SUCCESS', res.data);
      })
      .catch((err) => {
        context.commit('GET_PORTFOLIO_COMPARISON_GRAPH_INFO_ERROR', err);
      });
  },

  createPortfolio(context, payload) {
    context.commit('CREATE_PORTFOLIO_INIT');
    return this.dispatch('api/createPortfolio', payload)
      .then((res) => {
        context.commit('CREATE_PORTFOLIO_SUCCESS', res.data);
        this.dispatch(
          'portfolio/getMyPortfolios',
          (context, { noSkeletons: true })
        );
        if (payload.invitationId) {
          this.dispatch('leagues/answerLeagueInvitation', {
            leagueInvitationId: payload.invitationId,
            invitationAnswer: true,
          });
        }
        eventManager.raisePortfolioCreationEvent({
          portfolio_name: res.data.name,
          portfolio_creation_date: res.data.lastUpdated,
          portfolio_main_currency: res.data.currency,
          portfolio_privacy_type: res.data.isPublic ? 'Public' : 'Private',
          // TODO : discuss if we add cash info in the future
          portfolio_money_volume: res.data.cashItems.reduce(
            (acc, item) =>
              acc +
              item.amount *
                item.latestCashItemToPortfolioCurrencyConversionRate,
            0
          ),
        });
      })
      .catch((err) => {
        context.commit('CREATE_PORTFOLIO_ERROR', err);
      });
  },
  getMyPortfolios(context, payload) {
    const { noSkeletons } = payload ?? {};

    // NOTE: With this in place, we only show skeletons if we don't explicitly specify that we don't want skeletons,
    // AND the related data hasn't ALREADY been loaded.
    // This is to prevent unnecessary skeleton showing if the data has already been loaded.
    // Instead, we simply replace the existing data and the UI updates accordingly.
    if (!noSkeletons && context.state.getMyPortfolios.data === null)
      context.commit('SET_GET_MY_PORTFOLIOS_LOADING', true);

    return this.dispatch('api/getMyPortfolios')
      .then((res) => {
        context.commit('GET_MY_PORTFOLIOS_SUCCESS', res.data);
      })
      .catch((err) => {
        context.commit('GET_MY_PORTFOLIOS_ERROR', err);
      });
  },
  getPortfolioById(context, payload) {
    context.commit('GET_PORTFOLIO_BY_ID_INIT');
    return this.dispatch('api/getPortfolioById', payload)
      .then((res) => {
        context.commit('GET_PORTFOLIO_BY_ID_SUCCESS', res.data);
        return res.data;
      })
      .catch((err) => {
        context.commit('GET_PORTFOLIO_BY_ID_ERROR', err);
      });
  },
  updatePortfolioName(context, name) {
    context.commit('UPDATE_PORTFOLIO_NAME', name);
  },
  updatePortfolioDescription(context, description) {
    context.commit('UPDATE_PORTFOLIO_DESCRIPTION', description);
  },
  togglePortfolioRankNotifications(context, payload) {
    return this.dispatch('api/togglePortfolioRankNotifications', payload)
      .then((res) => {
        context.commit('TOGGLE_PORTFOLIO_RANK_NOTIFICATIONS', {
          value: res.data,
          portfolioId: payload.id,
        });
      })
      .catch((err) => {
        context.commit('TOGGLE_PORTFOLIO_RANK_NOTIFICATIONS_ERROR', err);
      });
  },

  validatePortfolioOperation(context, payload) {
    context.commit('VALIDATE_PORTFOLIO_OPERATION_INIT');
    const profileId = context.rootGetters['authentication/profileId'];
    return this.dispatch('api/validatePortfolioOperation', {
      operationDto: payload,
      commitTransaction: payload.commitTransaction,
      portfolioId: payload.portfolioId,
    })
      .then((res) => {
        if (res.data.transactionCommited) {
          payload.transactionType = PortfolioTransactionTypes.TRANSACTION;
          if (payload.operationType === 'Sell')
            payload.changePercent = getSellReturnPercent(
              res.data.portfolioDetails,
              payload.itemId
            );
          window.$nuxt.$notify.addTransactionSuccess(
            window.$nuxt.$t('operation_add_ok'),
            profileId,
            {},
            payload
          );

          context.commit('VALIDATE_PORTFOLIO_OPERATION_SUCCESS', res.data);
          context.commit('UPDATE_PORTFOLIO_LINES', res.data.portfolioDetails);

          if (
            context.state.getPortfolioById.data?.isListed === false &&
            res.data.portfolioDetails.isListed
          )
            showToast(window.$nuxt.$t('portfolio_go_ranked_message'));

          // Raise transaction completed event
          eventManager.raiseTransactionCompletedEvent({
            transaction_type: 'Assets',
            operation_type: payload.operationType,
            active_name: payload.companyName,
            currency: payload.chosenCurrency,
            action_quantity: payload.quantity,
            // This will always be the amount in the stock's currency
            // So if the user choses another currenyc to pay with, the total won't match the currency param here
            // TODO - Add some conversion information to handle this type of case
            transaction_total: payload.price * payload.quantity,
          });
        } else {
          context.commit('VALIDATE_PORTFOLIO_OPERATION_SUCCESS', res.data);
        }
      })
      .catch((err) => {
        context.commit('VALIDATE_PORTFOLIO_OPERATION_ERROR', err);
      })
      .finally(() => {
        context.commit('SET_VALIDATE_PORTFOLIO_OPERATION_LOADING', false);
      });
  },

  validatePortfolioItemAddition(context, payload) {
    context.commit('VALIDATE_PORTFOLIO_OPERATION_INIT');
    const profileId = context.rootGetters['authentication/profileId'];
    return this.dispatch('api/validatePortfolioOperation', {
      itemDto: payload,
      commitTransaction: payload.commitTransaction,
      portfolioId: payload.portfolioId,
    })
      .then((res) => {
        if (res.data.transactionCommited) {
          payload.transactionType = PortfolioTransactionTypes.TRANSACTION;
          window.$nuxt.$notify.addTransactionSuccess(
            window.$nuxt.$t('operation_add_ok'),
            profileId,
            {},
            payload
          );

          context.commit('VALIDATE_PORTFOLIO_OPERATION_SUCCESS', res.data);
          context.commit('UPDATE_PORTFOLIO_LINES', res.data.portfolioDetails);

          if (
            context.state.getPortfolioById.data?.isListed === false &&
            res.data.portfolioDetails.isListed
          )
            showToast(window.$nuxt.$t('portfolio_go_ranked_message'));

          // Raise transaction completed event
          eventManager.raiseTransactionCompletedEvent({
            transaction_type: 'Assets',
            operation_type: payload.operationType,
            active_name: payload.companyName,
            currency: payload.chosenCurrency,
            action_quantity: payload.quantity,
            // This will always be the amount in the stock's currency
            // So if the user choses another currenyc to pay with, the total won't match the currency param here
            // TODO - Add some conversion information to handle this type of case
            transaction_total: payload.price * payload.quantity,
          });
        } else {
          context.commit('VALIDATE_PORTFOLIO_OPERATION_SUCCESS', res.data);
        }
      })
      .catch((err) => {
        context.commit('VALIDATE_PORTFOLIO_OPERATION_ERROR', err);
      })
      .finally(() => {
        context.commit('SET_VALIDATE_PORTFOLIO_OPERATION_LOADING', false);
      });
  },
  getPortfolioOrders(context, payload) {
    const { noSkeletons } = payload ?? {};
    if (!noSkeletons && context.state.portfolioOrders.data === null)
      context.commit('SET_PORTFOLIO_ORDER_LOADING', true);

    return this.dispatch('api/getPortfolioOrders', payload)
      .then((res) => {
        context.commit('SET_PORTFOLIO_ORDERS', res.data);
      })
      .catch((err) => {
        context.commit('SET_PORTFOLIO_ORDER_ERROR', err);
      })
      .finally(() => {
        context.commit('SET_PORTFOLIO_ORDERS_LOADING', false);
      });
  },
  createPortfolioOrder(context, payload) {
    const { noSkeletons } = payload ?? {};
    if (!noSkeletons) context.commit('CREATE_PORTFOLIO_ORDER_LOADING', true);
    return this.dispatch('api/createPortfolioOrder', payload)
      .then((res) => {
        context.commit('CREATE_PORTFOLIO_ORDER_SUCCESS', res.data);
        context.commit('APPEND_PORTFOLIO_ORDER', res.data);

        const transactionPayload = {
          companyUri: res.data.stock.avatarUri,
          companyName: res.data.stock.companyName,
          symbolName: res.data.stock.name,
          operationType: res.data.operationType,
          changePercent: payload.changePercent,
          portfolioId: res.data.portfolioId,
          companyId: res.data.stock.companyProfileId,
          price: payload.price,
          currency: payload.tickerCurrency,
          transactionType: PortfolioTransactionTypes.ORDER,
        };

        window.$nuxt.$notify.addTransactionSuccess(
          window.$nuxt.$t('order_add_ok'),
          res.data.author.profileId,
          {},
          transactionPayload
        );
      })
      .catch((error) => {
        const customError = error.response?.data;
        if (
          customError?.statusCode === 400 &&
          customError?.errorCode === 'wal_400022'
        ) {
          window.$nuxt.$notify.error(
            window.$nuxt.$t('order_add_error_not_enough_cash')
          );
        } else if (
          customError?.statusCode === 400 &&
          customError?.errorCode === 'wal_400023'
        ) {
          window.$nuxt.$notify.error(
            window.$nuxt.$t('order_add_order_limit_reached')
          );
        } else {
          window.$nuxt.$notify.error(
            window.$nuxt.$t('order_add_error_general')
          );
        }
        context.commit('CREATE_PORTFOLIO_ORDER_ERROR', error);
      })
      .finally(() => {
        context.commit('CREATE_PORTFOLIO_ORDER_LOADING', false);
      });
  },
  cancelPortfolioOrder(context, payload) {
    const { noSkeletons } = payload ?? {};
    if (!noSkeletons) context.commit('CANCEL_PORTFOLIO_ORDER_LOADING', true);

    return this.dispatch('api/cancelPortfolioOrder', payload)
      .then((res) => {
        context.commit('CANCEL_PORTFOLIO_ORDER_SUCCESS', res.data);
        context.commit('CANCEL_PORTFOLIO_ORDER_ERROR', null);
        window.$nuxt.$notify.success(window.$nuxt.$t('order_cancel_success'));
      })
      .catch((err) => {
        context.commit('CANCEL_PORTFOLIO_ORDER_ERROR', err);
        window.$nuxt.$notify.error(window.$nuxt.$t('order_cancel_error'));
      })
      .finally(() => {
        context.commit('CANCEL_PORTFOLIO_ORDER_LOADING', false);
      });
  },
  ...ActionsCreator([
    {
      name: 'orderPortfolios',
      mutation: 'ORDER_PORTFOLIOS',
    },
    {
      name: 'getPortfolioGraph',
      mutation: 'GET_PORTFOLIO_GRAPH',
    },
  ]),
};
